/**
 * Global mutation-error policy — wired once into the app's `QueryClient` via
 * `new MutationCache({ onError: handleMutationError })` in `src/frontend.tsx`.
 *
 * Every failed mutation toasts its error message by default, so pages don't
 * repeat `onError: (err) => toast.error(err.message)`. A mutation opts out by
 * declaring its own `onError` (TanStack calls the cache handler IN ADDITION to
 * the per-mutation one, so this is the double-toast guard) or by setting
 * `meta: { silentError: true }`. `meta.errorMessage` is the fallback text when
 * the rejection carries no message (e.g. a non-Error throw).
 */
import { toast } from "sonner";

export type MutationErrorMeta = {
	/** Suppress the global toast (the caller surfaces the error itself). */
	silentError?: boolean;
	/** Toast text when the error has no message of its own. */
	errorMessage?: string;
};

declare module "@tanstack/react-query" {
	interface Register {
		mutationMeta: MutationErrorMeta;
	}
}

/** The slice of a TanStack `Mutation` the policy reads. */
type MutationLike = {
	options: { onError?: unknown; meta?: MutationErrorMeta };
};

const FALLBACK_MESSAGE = "Something went wrong";

/** Build the `MutationCache.onError` handler around an injectable notifier. */
export function createMutationErrorHandler(notify: (message: string) => void) {
	return (
		error: unknown,
		_variables: unknown,
		_onMutateResult: unknown,
		mutation: MutationLike,
	): void => {
		if (mutation.options.onError) return;
		const meta = mutation.options.meta;
		if (meta?.silentError) return;
		const own = error instanceof Error ? error.message : undefined;
		notify(own || meta?.errorMessage || FALLBACK_MESSAGE);
	};
}

export const handleMutationError = createMutationErrorHandler((message) => {
	toast.error(message);
});
